// src/redux/visitor/visitorSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../storeTypes";

import visitorApi from "../../services/visitor/visitorApi";
import type { VisitorProfile } from "../../types/profiles/visitorProfileTypes";
import type { MomentDTO, MomentFeedResponse } from "../../types/ugc/momentTypes";
import type { PrayerDTO, PrayerFeedResponse } from "../../types/ugc/prayerTypes";

// ---------------------------
// State types
// ---------------------------
type LoadStatus = "idle" | "loading" | "succeeded" | "failed";

type VisitorMomentsListState = {
  ids: string[];
  page: number;        // next page to request
  next: string | null; // DRF next url
  hasMore: boolean;
  loading: boolean;
  error?: string | null;
};

type VisitorPrayersListState = {
  ids: string[];
  page: number;
  next: string | null;
  hasMore: boolean;
  loading: boolean;
  error?: string | null;
};

type VisitorState = {
  // Profile cache by username
  profilesByUsername: Record<string, VisitorProfile>;
  profileStatusByUsername: Record<string, LoadStatus>;
  profileErrorByUsername: Record<string, string | null>;

  // Visitor moments cache
  momentsBySlug: Record<string, MomentDTO>;
  momentsListByUsername: Record<string, VisitorMomentsListState>;

  // Visitor prayers cache
  prayersBySlug: Record<string, PrayerDTO>;
  prayersListByUsername: Record<string, VisitorPrayersListState>;
};


const initialListState = (): VisitorMomentsListState => ({
  ids: [],
  page: 1,
  next: null,
  hasMore: true,
  loading: false,
  error: null,
});

const initialState: VisitorState = {
  profilesByUsername: {},
  profileStatusByUsername: {},
  profileErrorByUsername: {},

  momentsBySlug: {},
  momentsListByUsername: {},

  prayersBySlug: {},
  prayersListByUsername: {},
};

// ---------------------------
// Thunks
// ---------------------------

// Profile
export const fetchVisitorProfile = createAsyncThunk<
  { username: string; data: VisitorProfile },
  string,
  { rejectValue: { username: string; message: string } }
>("visitor/fetchProfile", async (username, { rejectWithValue }) => {
  try {
    const data = await visitorApi.getProfile(username);
    return { username, data };
  } catch (error: any) {
    const msg =
      error?.response?.data?.error ??
      error?.response?.data ??
      error?.message ??
      "Error fetching visitor profile.";
    return rejectWithValue({ username, message: String(msg) });
  }
});

// Moments of visited user (page-based)
export const fetchVisitorMoments = createAsyncThunk<
  { username: string; res: MomentFeedResponse; requestedPage: number },
  { username: string; page?: number } | { username: string; page?: number } & void,
  { state: RootState; rejectValue: { username: string; message: string } }
>("visitor/fetchMoments", async (arg: any, { getState, rejectWithValue }) => {
  const username = arg.username as string;

  const state = getState();
  const list = state.visitor?.momentsListByUsername?.[username];
  const pageFromState = list?.page ?? 1;
  const requestedPage =
    typeof arg?.page === "number" && arg.page > 0 ? arg.page : pageFromState;

  try {
    const res = await visitorApi.getMoments(username, { page: requestedPage });
    return { username, res, requestedPage };
  } catch (error: any) {
    const msg =
      error?.response?.data?.error ??
      error?.response?.data ??
      error?.message ??
      "Error fetching visitor moments.";
    return rejectWithValue({ username, message: String(msg) });
  }
});

// ---------------------------
// Internal helpers
// ---------------------------
const addUniqueBack = (arr: string[], id: string) => {
  if (!arr.includes(id)) arr.push(id);
};

const ensureList = (state: VisitorState, username: string) => {
  if (!state.momentsListByUsername[username]) {
    state.momentsListByUsername[username] = initialListState();
  }
  return state.momentsListByUsername[username];
};

const ensurePrayerList = (state: VisitorState, username: string) => {
  if (!state.prayersListByUsername[username]) {
    state.prayersListByUsername[username] = initialListState();
  }
  return state.prayersListByUsername[username];
};

export const fetchVisitorPrayers = createAsyncThunk<
  { username: string; res: PrayerFeedResponse; requestedPage: number },
  { username: string; page?: number },
  { state: RootState; rejectValue: { username: string; message: string } }
>("visitor/fetchPrayers", async (arg, { getState, rejectWithValue }) => {
  const username = arg.username;

  const state = getState();
  const list = state.visitor?.prayersListByUsername?.[username];
  const pageFromState = list?.page ?? 1;

  const requestedPage =
    typeof arg?.page === "number" && arg.page > 0
      ? arg.page
      : pageFromState;

  try {
    const res = await visitorApi.getPrayers(username, {
      page: requestedPage,
    });

    return { username, res, requestedPage };
  } catch (error: any) {
    const msg =
      error?.response?.data?.error ??
      error?.response?.data ??
      error?.message ??
      "Error fetching visitor prayers.";

    return rejectWithValue({ username, message: String(msg) });
  }
});

// ---------------------------
// Slice
// ---------------------------
const visitorSlice = createSlice({
  name: "visitor",
  initialState,
  reducers: {
    // Reset moments for a specific visited user
    resetVisitorMomentsFor(state, action: PayloadAction<{ username: string }>) {
      state.momentsListByUsername[action.payload.username] = initialListState();
    },

    // Optional: clear one cached profile
    clearVisitorProfile(state, action: PayloadAction<{ username: string }>) {
      const u = action.payload.username;
      delete state.profilesByUsername[u];
      delete state.profileStatusByUsername[u];
      delete state.profileErrorByUsername[u];
    },
  },
  extraReducers: (builder) => {
    // -----------------------
    // Profile cases
    // -----------------------
    builder
      .addCase(fetchVisitorProfile.pending, (state, action) => {
        const username = action.meta.arg;
        state.profileStatusByUsername[username] = "loading";
        state.profileErrorByUsername[username] = null;
      })
      .addCase(fetchVisitorProfile.fulfilled, (state, action) => {
        const { username, data } = action.payload;
        state.profilesByUsername[username] = data;
        state.profileStatusByUsername[username] = "succeeded";
      })
      .addCase(fetchVisitorProfile.rejected, (state, action) => {
        const payload = action.payload;
        const username = payload?.username ?? action.meta.arg;
        state.profileStatusByUsername[username] = "failed";
        state.profileErrorByUsername[username] =
          payload?.message ?? action.error.message ?? "Failed to load profile.";
      });

    // -----------------------
    // Moments cases
    // -----------------------
    builder
      .addCase(fetchVisitorMoments.pending, (state, action) => {
        const username = (action.meta.arg as any).username as string;
        const list = ensureList(state, username);
        list.loading = true;
        list.error = null;
      })
      .addCase(fetchVisitorMoments.fulfilled, (state, action) => {
        const { username, res, requestedPage } = action.payload;
        const { results, next } = res;

        const list = ensureList(state, username);

        // Refresh if page=1
        if (requestedPage <= 1) {
          list.ids = [];
          list.page = 1;
          list.next = null;
          list.hasMore = true;
        }

        results.forEach((m) => {
          state.momentsBySlug[m.slug] = m;
          addUniqueBack(list.ids, m.slug);
        });

        list.next = next ?? null;
        list.hasMore = Boolean(next);

        // Advance only if more pages exist
        if (list.hasMore) {
          list.page = requestedPage + 1;
        }

        list.loading = false;
      })
      .addCase(fetchVisitorMoments.rejected, (state, action) => {
        const payload = action.payload as any;
        const username = payload?.username ?? (action.meta.arg as any).username;
        const list = ensureList(state, username);

        list.loading = false;
        list.error =
          payload?.message ?? action.error.message ?? "Failed to load moments.";
      });

    // -----------------------
    // Prayers cases
    // -----------------------
    builder
      .addCase(fetchVisitorPrayers.pending, (state, action) => {
        const username = action.meta.arg.username;
        const list = ensurePrayerList(state, username);
        list.loading = true;
        list.error = null;
      })
      .addCase(fetchVisitorPrayers.fulfilled, (state, action) => {
        const { username, res, requestedPage } = action.payload;
        const { results, next } = res;

        const list = ensurePrayerList(state, username);

        // Refresh if page=1
        if (requestedPage <= 1) {
          list.ids = [];
          list.page = 1;
          list.next = null;
          list.hasMore = true;
        }

        results.forEach((p) => {
          state.prayersBySlug[p.slug] = p;
          addUniqueBack(list.ids, p.slug);
        });

        list.next = next ?? null;
        list.hasMore = Boolean(next);

        if (list.hasMore) {
          list.page = requestedPage + 1;
        }

        list.loading = false;
      })
      .addCase(fetchVisitorPrayers.rejected, (state, action) => {
        const payload = action.payload as any;
        const username =
          payload?.username ?? action.meta.arg.username;

        const list = ensurePrayerList(state, username);
        list.loading = false;
        list.error =
          payload?.message ??
          action.error.message ??
          "Failed to load prayers.";
      });

  },
});

export const { resetVisitorMomentsFor, clearVisitorProfile } =
  visitorSlice.actions;

export default visitorSlice.reducer;
